import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { acknowledgeCancelledItem } from "../../api/batches";
import {
  ITEM_CANCELLED_EVENT,
  cancelledItemStage,
  cancelledItemTitle,
} from "../../helpers/cancelledItemAlert";
import { beepBad, vibrateBad } from "../../helpers/handheldFeedback";

const formatWhen = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const Row = ({ label, children }) => (
  <Box sx={{ display: "flex", gap: 1.5, py: 0.5 }}>
    <Typography variant="body2" sx={{ color: "text.secondary", minWidth: 96 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {children}
    </Typography>
  </Box>
);

/**
 * Global popup for a scanned item that was cancelled or removed in the store
 * after batching. Mounted once in App; any station request that gets
 * ITEM_CANCELLED opens it (see helpers/cancelledItemAlert + api/ajax).
 *
 * Blocking items: one button, "Got it". Warning items (setting off, whole
 * order cancelled): "Set aside" or "Continue anyway", which lets the next
 * scan through and is logged on the order.
 */
export default function CancelledItemDialog() {
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onCancelled = (event) => {
      const next = event?.detail;
      if (!next?.unitId) return;
      setError("");
      setItem(next);
      try {
        beepBad();
        vibrateBad();
      } catch {
        /* audio blocked */
      }
    };
    window.addEventListener(ITEM_CANCELLED_EVENT, onCancelled);
    return () => window.removeEventListener(ITEM_CANCELLED_EVENT, onCancelled);
  }, []);

  const close = useCallback(
    async ({ override = false } = {}) => {
      if (!item) return;
      setBusy(true);
      setError("");
      try {
        await acknowledgeCancelledItem(item.unitId, { override });
        setItem(null);
      } catch (err) {
        if (override) {
          setError(
            err?.response?.data?.message || "Could not continue. Try again."
          );
        } else {
          // Seeing the popup is what matters; a failed log must not trap the floor.
          setItem(null);
        }
      } finally {
        setBusy(false);
      }
    },
    [item]
  );

  if (!item) return null;
  const blocking = item.blocking !== false;
  const when = formatWhen(item.cancelledAt);

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={() => {
        /* backdrop clicks don't dismiss: the floor must answer it */
      }}
      aria-labelledby="cancelled-item-title"
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <Box
        id="cancelled-item-title"
        sx={{
          bgcolor: blocking ? "error.main" : "warning.main",
          color: blocking ? "error.contrastText" : "warning.contrastText",
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {blocking ? "This item has been cancelled" : "This order was cancelled"}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.95 }}>
          {blocking ? "Set this item aside. Do not ship." : "Set this item aside unless you mean to continue."}
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>{cancelledItemTitle(item)}</Typography>
        {item.orderNumber && <Row label="Order">{item.orderNumber}</Row>}
        <Row label="Why">
          {item.reasonLabel || "Cancelled in the store"}
          {when ? ` · ${when}` : ""}
        </Row>
        {item.batchNumber && <Row label="Batch">{item.batchNumber}</Row>}
        <Row label="Status">{cancelledItemStage(item)}</Row>
        <Row label="Barcode">{item.unitId}</Row>
        {item.replacement && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Replaced by {item.replacement}. That item is made separately.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {blocking ? (
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={() => close()}
            autoFocus
            fullWidth
          >
            Got it
          </Button>
        ) : (
          <>
            <Button variant="text" color="inherit" disabled={busy} onClick={() => close({ override: true })}>
              Continue anyway
            </Button>
            <Button variant="contained" color="warning" disabled={busy} onClick={() => close()} autoFocus>
              Set aside
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
